/**
 * NECS Resident App — mobile-first PWA shell at /resident.
 * Auth gate (login/register with community code) + bottom-nav app:
 * Home · Alerts · Report · Profile.
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Home, Bell, AlertTriangle, History, User as UserIcon, Trophy, Megaphone } from 'lucide-react';
import { Toaster } from 'sonner';
import { apiFetch } from '../../lib/apiFetch';
import { useAuth } from '../../contexts/AuthContext';
import ResidentAuth from './ResidentAuth';
import ResidentHome from './ResidentHome';
import ResidentNotifications from './ResidentNotifications';
import ResidentViolationForm from './ResidentViolationForm';
import ResidentHistory from './ResidentHistory';
import ResidentOperationRecord from './ResidentOperationRecord';
import ResidentProfile from './ResidentProfile';
import UpdatesNoticesPanel, { useUpdatesUnread, notifyUpdatesChanged } from '../../components/updates/UpdatesNoticesPanel';
import UpdatesBlockingGate from '../../components/updates/UpdatesBlockingGate';
import StreakCelebration from '../../components/updates/StreakCelebration';

const GOLD = '#C9A962';
const STATUS_COLORS = { good: '#22c55e', at_risk: '#f59e0b', violation: '#ef4444' };
const TABS = [
  { key: 'home', label: 'Home', Icon: Home },
  { key: 'alerts', label: 'Alerts', Icon: Bell },
  { key: 'report', label: 'Report', Icon: AlertTriangle },
  { key: 'history', label: 'History', Icon: History },
  { key: 'updates', label: 'Updates', Icon: Megaphone },
  { key: 'profile', label: 'Profile', Icon: UserIcon },
];

export default function ResidentAppPage() {
  const { user, setUser } = useAuth();
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState('home');
  const [home, setHome] = useState(null);
  const [recStatus, setRecStatus] = useState('good');
  const [recordOpen, setRecordOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const updatesUnread = useUpdatesUnread();

  const loadHome = useCallback(async () => {
    if (user?.role === 'tenant') return;
    try {
      const data = await apiFetch('/api/resident/home');
      setHome(data);
      setMe(data.resident);
    } catch (e) {
      setMe(null);
    }
  }, [user?.role]);

  useEffect(() => { loadHome(); }, [loadHome]);

  // On app open: compliance status for the header trophy + check for newly
  // earned streak milestone badges (confetti celebration, dismiss on tap).
  useEffect(() => {
    if (!me) return;
    notifyUpdatesChanged();
    (async () => {
      try {
        const rec = await apiFetch('/api/resident/operation-record');
        if (rec.open_violations > 0) setRecStatus('violation');
        else if ((rec.totals?.violations_on_account || 0) > 0) setRecStatus('at_risk');
        else setRecStatus('good');
        if (rec.new_badges && rec.new_badges.length > 0) setCelebrate(rec.new_badges);
      } catch (_) { /* noop */ }
    })();
  }, [me]);

  if (user?.role === 'tenant') return <Navigate to="/tenant-dashboard" replace />;
  if (me === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#141414' }}>
        <p className="text-sm uppercase tracking-[0.3em]" style={{ color: GOLD }}>NECS Resident</p>
      </div>
    );
  }

  if (!me) {
    return <ResidentAuth onAuthed={() => { setMe(undefined); loadHome(); }} />;
  }

  const unread = home?.unread_count || 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#141414' }} data-testid="resident-app-shell">
      <Toaster position="top-center" richColors />
      <UpdatesBlockingGate />
      {celebrate && <StreakCelebration badges={celebrate} onDismiss={() => setCelebrate(null)} />}
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-zinc-800 backdrop-blur-md" style={{ backgroundColor: 'rgba(20,20,20,0.92)' }}>
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="min-w-0 flex-1 mr-2">
            <p className="text-[10px] uppercase tracking-[0.35em] text-zinc-500" data-testid="resident-app-term">NECS {home?.property?.occupant_term || 'Resident'}</p>
            <p className="text-sm font-black uppercase tracking-wide text-white break-words" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="resident-property-name">
              {home?.property?.property_name || me.property_name}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setTab('profile')} className="flex items-center" data-testid="resident-avatar-btn" title="Profile">
              <span className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-black uppercase"
                style={{ backgroundColor: 'rgba(201,169,98,0.15)', color: GOLD, border: `1px solid ${GOLD}` }}>
                {(me.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
              </span>
            </button>
            <button onClick={() => setTab('alerts')} className="relative p-2" data-testid="resident-bell-btn">
              <Bell className="h-5 w-5" style={{ color: GOLD }} />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid="resident-unread-badge">
                  {unread}
                </span>
              )}
            </button>
            {/* Compliance status trophy — tappable. Green = good standing ·
                yellow = at-risk · red = active violation. Section 11 —
                tapping the trophy opens an overlay with the operations
                record (streaks, totals, milestone badges). */}
            <button
              type="button"
              onClick={() => setRecordOpen(true)}
              className="p-2 inline-flex bg-transparent border-0 cursor-pointer"
              data-testid="resident-status-trophy"
              data-status={recStatus}
              title={recStatus === 'violation' ? 'Active violation · view record' : recStatus === 'at_risk' ? 'At risk · view record' : 'Good standing · view record'}
              aria-label="Open operations record"
            >
              <Trophy className="h-5 w-5" style={{ color: STATUS_COLORS[recStatus] }} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 pt-4 pb-24">
        {tab === 'home' && <ResidentHome home={home} onSubmitViolation={() => setTab('report')} onRefresh={loadHome} />}
        {tab === 'alerts' && <ResidentNotifications onChanged={loadHome} />}
        {tab === 'report' && <ResidentViolationForm violationTypes={home?.violation_types || []} onSubmitted={() => { loadHome(); setTab('home'); }} />}
        {tab === 'history' && <ResidentHistory />}
        {tab === 'updates' && <UpdatesNoticesPanel testIdPrefix="resident-updates" />}
        {tab === 'record' && <ResidentOperationRecord />}
        {tab === 'profile' && <ResidentProfile me={me} onChanged={loadHome} onLoggedOut={() => { setMe(null); setUser(null); }} onOpenRecord={() => setTab('record')} />}
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-20 border-t border-zinc-800" style={{ backgroundColor: 'rgba(20,20,20,0.97)' }} data-testid="resident-bottom-nav">
        <div className="max-w-md mx-auto grid grid-cols-6">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className="flex flex-col items-center gap-0.5 py-2.5 relative"
              data-testid={`resident-tab-${key}`}>
              <Icon className="h-5 w-5" style={{ color: tab === key ? GOLD : '#71717a' }} />
              <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: tab === key ? GOLD : '#71717a' }}>{label}</span>
              {key === 'alerts' && unread > 0 && (
                <span className="absolute top-1 right-[22%] h-2 w-2 rounded-full bg-red-600" />
              )}
              {key === 'updates' && updatesUnread > 0 && (
                <span className="absolute top-0.5 right-[14%] h-4 min-w-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid="resident-updates-unread-badge">
                  {updatesUnread}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Section 11 — Operations Record overlay opened by the resident
          compliance trophy. Read-only reload of the record. */}
      {recordOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setRecordOpen(false)}
          data-testid="resident-record-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="resident-record-board"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-6 w-6" style={{ color: STATUS_COLORS[recStatus] }} />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Operations Record</p>
                  <p className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {recStatus === 'violation' ? 'ACTIVE VIOLATION' : recStatus === 'at_risk' ? 'AT RISK' : 'GOOD STANDING'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRecordOpen(false)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white text-xl leading-none"
                aria-label="Close operations record"
                data-testid="resident-record-close"
              >
                ×
              </button>
            </div>
            <ResidentOperationRecord />
          </div>
        </div>
      )}
    </div>
  );
}
